import { useCallback, useEffect, useRef, useState } from 'react';
import {
  generateShareLink,
  listShareLinks,
  revokeShareLink,
  shareErrorMessage,
  type ExpiryDays,
  type ShareErrorCode,
} from '../lib/shareApi';
import { formatLongDate } from '@/features/timeline/lib/formatDate';
import type { ShareLink } from '@/types/db';

interface Props {
  profileId: string;
  profileName: string;
  onClose: () => void;
}

type GenState =
  | { kind: 'idle' }
  | { kind: 'generating' }
  | { kind: 'ready'; url: string; token: string }
  | { kind: 'error'; message: string };

type LinksState =
  | { kind: 'loading' }
  | { kind: 'ready'; links: ShareLink[] }
  | { kind: 'error' };

const EXPIRY_OPTIONS: { value: ExpiryDays; label: string }[] = [
  { value: 1, label: '1 day' },
  { value: 3, label: '3 days' },
  { value: 7, label: '7 days' },
];

function slugifyName(name: string): string {
  return name.trim().replace(/\s+/g, '-');
}

function makeShareUrl(token: string, profileName: string): string {
  return `${window.location.origin}/share/${token}/${slugifyName(profileName)}`;
}

function whatsappHref(shareUrl: string, firstName: string): string {
  return `https://wa.me/?text=${encodeURIComponent(`Here is ${firstName}'s medical records: ${shareUrl}`)}`;
}

function formatRelativeTime(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return 'Expired';
  const hours = Math.floor(diff / (60 * 60 * 1000));
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  return hours < 24 ? `${hours}h left` : `${days}d left`;
}

/* ---- Inline icons (no library) ---------------------------------------- */

function IconX() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconEye({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconEyeOff() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconDots() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  );
}

function IconWhatsApp({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

/* ---- Component --------------------------------------------------------- */

export function ShareDoctorModal({ profileId, profileName, onClose }: Props) {
  const [gen, setGen] = useState<GenState>({ kind: 'idle' });
  const [links, setLinks] = useState<LinksState>({ kind: 'loading' });
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeErrors, setRevokeErrors] = useState<Record<string, string>>({});
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [expiresInDays, setExpiresInDays] = useState<ExpiryDays>(7);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const profileFirstName = profileName.split(' ')[0];
  const menuRef = useRef<HTMLDivElement>(null);

  const loadLinks = useCallback(async () => {
    setLinks({ kind: 'loading' });
    try {
      const rows = await listShareLinks(profileId);
      setLinks({ kind: 'ready', links: rows });
    } catch (err) {
      console.error('[share-modal] list failed', err);
      setLinks({ kind: 'error' });
    }
  }, [profileId]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (openMenuId) { setOpenMenuId(null); return; }
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, openMenuId]);

  useEffect(() => {
    if (!openMenuId) return;
    function onOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [openMenuId]);

  async function handleGenerate() {
    if (gen.kind === 'generating') return;
    setGen({ kind: 'generating' });
    const outcome = await generateShareLink(profileId, {
      expiresInDays,
      password: password.trim() || undefined,
    });
    if (outcome.success) {
      setGen({ kind: 'ready', url: outcome.url, token: outcome.token });
      setPassword('');
      setShowAdvanced(false);
      loadLinks();
    } else {
      setGen({ kind: 'error', message: shareErrorMessage(outcome.error as ShareErrorCode) });
    }
  }

  async function handleCopy(url: string) {
    setCopyFailed(false);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2200);
        return;
      }
      throw new Error('clipboard_unavailable');
    } catch {
      setCopyFailed(true);
    }
  }

  async function handleRevoke(linkId: string) {
    setRevokingId(linkId);
    setRevokeErrors((prev) => { const n = { ...prev }; delete n[linkId]; return n; });
    try {
      await revokeShareLink(linkId);
      await loadLinks();
    } catch (err) {
      const msg =
        err instanceof Error && err.message === 'revoke_no_match'
          ? "Couldn't revoke — refresh and try again."
          : "Couldn't revoke. Please try again.";
      setRevokeErrors((prev) => ({ ...prev, [linkId]: msg }));
    } finally {
      setRevokingId(null);
    }
  }

  const now = Date.now();
  const activeLinks =
    links.kind === 'ready'
      ? links.links.filter((l) => !l.revoked_at && new Date(l.expires_at).getTime() > now)
      : [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share with a doctor"
      className="fixed inset-0 z-50 bg-ink/60 flex items-end md:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-surface w-full md:max-w-md md:rounded-card rounded-t-[20px] shadow-pop max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-surface z-10">
          <h2 className="font-bold text-base">Share with a doctor</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="min-w-tap min-h-tap flex items-center justify-center text-ink-muted -mr-1 rounded-lg"
          >
            <IconX />
          </button>
        </header>

        <div className="px-4 py-5 space-y-5">

          {/* ---- Generate form ---- */}
          {gen.kind !== 'ready' && (
            <div className="space-y-3">
              <p className="text-sm text-ink-muted leading-relaxed">
                Generates a time-limited read-only link with a clinical summary of{' '}
                <span className="font-medium text-ink">{profileFirstName}</span>'s records.
              </p>

              {/* Expiry inline */}
              <div className="flex items-center gap-3">
                <label htmlFor="expiry-select" className="text-sm text-ink-muted whitespace-nowrap shrink-0">
                  Expires after
                </label>
                <select
                  id="expiry-select"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value) as ExpiryDays)}
                  className="input flex-1"
                  disabled={gen.kind === 'generating'}
                >
                  {EXPIRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Advanced toggle */}
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                aria-expanded={showAdvanced}
                aria-controls="share-password"
                className="text-xs text-ink-muted underline underline-offset-2 hover:text-ink min-h-tap inline-flex items-center"
              >
                {showAdvanced ? 'Remove password' : 'Add password protection'}
              </button>

              {showAdvanced && (
                <div className="relative">
                  <input
                    id="share-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password (optional)"
                    aria-label="Password protection (optional)"
                    className="input w-full pr-11"
                    disabled={gen.kind === 'generating'}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-0 top-0 min-w-tap min-h-tap flex items-center justify-center text-ink-muted"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <IconEyeOff /> : <IconEye className="h-4 w-4" />}
                  </button>
                </div>
              )}

              {gen.kind === 'error' && (
                <p className="text-sm text-flag-block" role="alert">{gen.message}</p>
              )}

              <button
                type="button"
                onClick={handleGenerate}
                disabled={gen.kind === 'generating'}
                className="btn-primary w-full"
              >
                {gen.kind === 'generating' ? 'Generating summary…' : 'Generate link'}
              </button>
            </div>
          )}

          {/* ---- Ready state ---- */}
          {gen.kind === 'ready' && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  id="generated-url"
                  type="text"
                  readOnly
                  value={gen.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="input flex-1 text-sm"
                />
                <button
                  type="button"
                  onClick={() => handleCopy(gen.url)}
                  className="btn-primary shrink-0 min-w-[80px]"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              {copyFailed && (
                <p className="text-xs text-flag-warn">
                  Couldn't copy — tap the link above and copy manually.
                </p>
              )}
              <a
                href={whatsappHref(gen.url, profileFirstName)}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
              >
                <IconWhatsApp className="h-4 w-4 text-green-600" />
                Send via WhatsApp
              </a>
              <div className="flex items-center justify-between pt-0.5">
                <p className="text-xs text-ink-muted">
                  Expires in {expiresInDays} day{expiresInDays !== 1 ? 's' : ''}
                </p>
                <button
                  type="button"
                  onClick={() => setGen({ kind: 'idle' })}
                  className="text-xs text-ink-muted underline underline-offset-2 hover:text-ink min-h-tap inline-flex items-center"
                >
                  Generate another
                </button>
              </div>
            </div>
          )}

          {/* ---- Active links ---- */}
          <section className="border-t border-border pt-4">
            <h3 className="text-sm font-medium text-ink mb-3">Active links</h3>

            {links.kind === 'loading' && (
              <div className="space-y-2">
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="h-[68px] rounded-lg bg-shimmer-line bg-shimmer animate-shimmer"
                    style={{ backgroundSize: '200% 100%' }}
                  />
                ))}
              </div>
            )}

            {links.kind === 'error' && (
              <div className="flex items-center gap-3">
                <p className="text-sm text-flag-block">Couldn't load links.</p>
                <button
                  type="button"
                  onClick={loadLinks}
                  className="text-sm text-accent underline underline-offset-2 shrink-0"
                >
                  Try again
                </button>
              </div>
            )}

            {links.kind === 'ready' && activeLinks.length === 0 && (
              <p className="text-sm text-ink-muted">No active links yet.</p>
            )}

            {links.kind === 'ready' && activeLinks.length > 0 && (
              <ul className="space-y-2">
                {activeLinks.map((link) => {
                  const url = makeShareUrl(link.token, profileName);
                  const cardError = revokeErrors[link.id];
                  const isMenuOpen = openMenuId === link.id;

                  return (
                    <li key={link.id} className="border border-border rounded-xl overflow-visible">
                      {/* Meta row */}
                      <div className="flex items-center gap-2 px-3 pt-2.5 pb-1.5 flex-wrap">
                        <span className="text-xs font-semibold text-ink">
                          {formatRelativeTime(link.expires_at)}
                        </span>
                        <span className="text-border-strong text-xs" aria-hidden="true">·</span>
                        <span className="text-xs text-ink-muted">
                          {formatLongDate(link.created_at.slice(0, 10))}
                        </span>
                        <span className="text-border-strong text-xs" aria-hidden="true">·</span>
                        <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                          <IconEye />
                          <span>{link.views_count ?? 0}</span>
                          <span className="sr-only">views</span>
                        </span>
                        {link.password_hash && (
                          <>
                            <span className="text-border-strong text-xs" aria-hidden="true">·</span>
                            <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                              <IconLock />
                              <span className="sr-only">Password protected</span>
                            </span>
                          </>
                        )}
                      </div>

                      {/* Actions row */}
                      <div className="flex items-center gap-1.5 px-3 pb-2.5">
                        <button
                          type="button"
                          onClick={() => handleCopy(url)}
                          className="btn-secondary text-sm flex-1"
                        >
                          Copy link
                        </button>
                        <a
                          href={whatsappHref(url, profileFirstName)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1.5"
                        >
                          <IconWhatsApp className="h-3.5 w-3.5 text-green-600" />
                          WhatsApp
                        </a>
                        {/* Overflow menu */}
                        <div className="relative" ref={isMenuOpen ? menuRef : undefined}>
                          <button
                            type="button"
                            onClick={() => setOpenMenuId(isMenuOpen ? null : link.id)}
                            aria-label="More options"
                            aria-expanded={isMenuOpen}
                            className="min-w-tap min-h-tap flex items-center justify-center rounded-lg border border-border text-ink-muted hover:bg-canvas"
                          >
                            <IconDots />
                          </button>
                          {isMenuOpen && (
                            <div className="absolute right-0 bottom-full mb-1 w-36 bg-surface border border-border rounded-lg shadow-pop z-20 overflow-hidden">
                              <button
                                type="button"
                                onClick={() => { setOpenMenuId(null); handleRevoke(link.id); }}
                                disabled={revokingId === link.id}
                                aria-label={`Revoke link created ${formatLongDate(link.created_at.slice(0, 10))}`}
                                className="w-full text-left px-3 py-2.5 text-sm text-flag-block hover:bg-canvas disabled:opacity-50"
                              >
                                {revokingId === link.id ? 'Revoking…' : 'Revoke link'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {cardError && (
                        <p className="text-xs text-flag-block px-3 pb-2.5 -mt-1" role="alert">
                          {cardError}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
